/**
 * Queue- und Quellen-Verwaltung (Spec 7.3, Admin-Bereich).
 *
 * API-Key **und** Webhook-Secret einer Quelle werden **einmalig** nach dem
 * Erzeugen bzw. Rotieren angezeigt (Spec 11) — danach existiert vom Key nur
 * noch sein Hash und vom Secret nur der Chiffretext. Das steht auch so im UI,
 * damit niemand spaeter danach sucht.
 *
 * Ab Phase 3 haengt an jeder Quelle mehr als ein Name: Rueckkanal
 * (`webhook_url` + Secret), Metadaten-Schema und Anzeige-Template. Das steht
 * in einem ausklappbaren Bereich je Quelle (`SourceSettings`), damit die
 * Liste eine Liste bleibt.
 */
import {
  SOURCE_TYPES,
  type Queue,
  type QueueSettings,
  type Source,
  type SourceType,
  type Team,
  type TenantMemberDetail,
  type WebhookDelivery,
} from '@tickethub/shared';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueueWorkflowSettings } from '@/components/QueueWorkflowSettings';
import { SecretOnce, SourceSettings } from '@/components/SourceSettings';
import { WebhookDeliveries } from '@/components/WebhookDeliveries';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { toastError } from '@/lib/errors';
import { useSession } from '@/lib/session';
import { membersApi, queuesApi, sourcesApi, teamsApi, webhookDeliveriesApi } from '@/lib/tickets';

/** Was gerade einmalig angezeigt wird — Key **oder** Secret, nie beides. */
type IssuedSecret = { kind: 'api_key' | 'webhook_secret'; value: string };

export function QueuesPage() {
  const { t } = useTranslation();
  const { activeMembership } = useSession();
  const tenantId = activeMembership?.tenant_id ?? null;
  const isAdmin = activeMembership?.role === 'admin';

  const [queues, setQueues] = useState<Queue[] | null>(null);
  const [sources, setSources] = useState<Source[]>([]);
  const [members, setMembers] = useState<TenantMemberDetail[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  /** Welche Queue ihre Workflow-Einstellungen offen hat (Spec 8.3/8.4). */
  const [workflowFor, setWorkflowFor] = useState<string | null>(null);
  const [queueName, setQueueName] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [sourceType, setSourceType] = useState<SourceType>('app');
  const [sourceQueueId, setSourceQueueId] = useState('');
  const [issued, setIssued] = useState<IssuedSecret | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [deliveriesFor, setDeliveriesFor] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[] | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [queueResult, sourceResult, teamResult] = await Promise.all([
        queuesApi.list(tenantId),
        sourcesApi.list(tenantId),
        teamsApi.list(tenantId),
      ]);
      setQueues(queueResult.items);
      setSources(sourceResult.items);
      setTeams(teamResult.items);

      try {
        setMembers((await membersApi.list(tenantId)).items);
      } catch {
        setMembers([]);
      }
    } catch (error) {
      setQueues([]);
      toastError(error, t);
    }
  }, [tenantId, t]);

  useEffect(() => {
    // `reload` setzt State erst *nach* dem Netzwerk-Await — es entsteht keine
    // kaskadierende Render-Schleife. Die Regel kann das nicht unterscheiden.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void reload();
  }, [reload]);

  /** Ohne eigene Wahl gilt die erste Queue — sonst waere das Formular blockiert. */
  const effectiveQueueId = sourceQueueId || (queues?.[0]?.id ?? '');

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    try {
      await action();
      await reload();
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  /** Zustellprotokoll einer Quelle nachladen (Spec 5.3). */
  const loadDeliveries = useCallback(
    async (sourceId: string) => {
      if (!tenantId) return;
      setDeliveriesFor(sourceId);
      setDeliveries(null);
      try {
        const page = await webhookDeliveriesApi.list(tenantId, { source_id: sourceId });
        setDeliveries(page.items);
      } catch (error) {
        setDeliveries([]);
        toastError(error, t);
      }
    },
    [tenantId, t],
  );

  if (!activeMembership) {
    return (
      <Alert>
        <AlertDescription>{t('tenant.empty')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <h1 className="text-2xl font-semibold">{t('queue.title')}</h1>

        {isAdmin ? (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (queueName.trim().length === 0 || !tenantId) return;
              void run(async () => {
                await queuesApi.create(tenantId, queueName.trim());
                setQueueName('');
              });
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="queue-name">{t('queue.name')}</Label>
              <Input
                id="queue-name"
                value={queueName}
                onChange={(event) => setQueueName(event.target.value)}
              />
            </div>
            <Button type="submit" disabled={busy}>
              {t('queue.create')}
            </Button>
          </form>
        ) : null}

        {queues === null ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : queues.length === 0 ? (
          <Alert>
            <AlertDescription>{t('queue.empty')}</AlertDescription>
          </Alert>
        ) : (
          <ul className="space-y-2">
            {queues.map((queue) => (
              <li key={queue.id} className="space-y-3 rounded-lg border p-3">
                <div className="flex items-center gap-3">
                  <span className="flex-1 font-medium">{queue.name}</span>
                  {isAdmin ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setWorkflowFor((current) => (current === queue.id ? null : queue.id))
                      }
                    >
                      {workflowFor === queue.id ? (
                        <ChevronDown aria-hidden="true" />
                      ) : (
                        <ChevronRight aria-hidden="true" />
                      )}
                      {t('queue.workflow')}
                    </Button>
                  ) : null}
                  {isAdmin ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={busy}
                      aria-label={t('queue.delete', { name: queue.name })}
                      onClick={() => void run(() => queuesApi.remove(tenantId ?? '', queue.id))}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  ) : null}
                </div>

                {workflowFor === queue.id ? (
                  <QueueWorkflowSettings
                    queue={queue}
                    members={members}
                    teams={teams}
                    disabled={!isAdmin || busy}
                    onSave={async (settings: QueueSettings) => {
                      await run(() => queuesApi.update(tenantId ?? '', queue.id, { settings }));
                    }}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-2xl font-semibold">{t('source.title')}</h2>

        {issued ? (
          <SecretOnce
            title={issued.kind === 'api_key' ? t('source.key_once_title') : t('source.secret_once_title')}
            hint={issued.kind === 'api_key' ? t('source.key_once_hint') : t('source.secret_once_hint')}
            value={issued.value}
            onClose={() => setIssued(null)}
          />
        ) : null}

        {isAdmin && queues && queues.length > 0 ? (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (sourceName.trim().length === 0 || !tenantId || effectiveQueueId === '') return;
              void run(async () => {
                const result = await sourcesApi.create(tenantId, {
                  type: sourceType,
                  name: sourceName.trim(),
                  default_queue_id: effectiveQueueId,
                });
                setSourceName('');
                if (result.api_key !== null) {
                  setIssued({ kind: 'api_key', value: result.api_key });
                }
              });
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="source-name">{t('source.name')}</Label>
              <Input
                id="source-name"
                value={sourceName}
                onChange={(event) => setSourceName(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="source-type">{t('source.type')}</Label>
              <Select
                id="source-type"
                value={sourceType}
                onChange={(event) => setSourceType(event.target.value as SourceType)}
              >
                {SOURCE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`source.type_${type}`)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="source-queue">{t('source.default_queue')}</Label>
              <Select
                id="source-queue"
                value={effectiveQueueId}
                onChange={(event) => setSourceQueueId(event.target.value)}
              >
                {queues.map((queue) => (
                  <option key={queue.id} value={queue.id}>
                    {queue.name}
                  </option>
                ))}
              </Select>
            </div>
            <Button type="submit" disabled={busy}>
              {t('source.create')}
            </Button>
          </form>
        ) : null}

        {sources.length === 0 ? (
          <Alert>
            <AlertDescription>{t('source.empty')}</AlertDescription>
          </Alert>
        ) : (
          <ul className="space-y-2">
            {sources.map((source) => (
              <li key={source.id} className="space-y-3 rounded-lg border p-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-medium">{source.name}</span>
                  <Badge variant="outline">{t(`source.type_${source.type}`)}</Badge>
                  <Badge variant={source.has_api_key ? 'secondary' : 'outline'}>
                    {source.has_api_key ? t('source.key_set') : t('source.key_missing')}
                  </Badge>
                  {source.webhook_url ? <Badge variant="secondary">{t('source.webhook_set')}</Badge> : null}

                  {isAdmin ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto"
                      aria-expanded={expanded === source.id}
                      onClick={() => setExpanded(expanded === source.id ? null : source.id)}
                    >
                      {expanded === source.id ? (
                        <ChevronDown aria-hidden="true" />
                      ) : (
                        <ChevronRight aria-hidden="true" />
                      )}
                      {t('source.settings')}
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="ml-auto"
                      disabled
                      aria-label={t('source.delete', { name: source.name })}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  )}
                </div>

                {isAdmin && expanded === source.id ? (
                  <SourceSettings
                    source={source}
                    busy={busy}
                    onSave={(input) =>
                      run(() => sourcesApi.update(tenantId ?? '', source.id, input))
                    }
                    onRotateKey={() =>
                      run(async () => {
                        const result = await sourcesApi.rotateKey(tenantId ?? '', source.id);
                        if (result.api_key !== null) {
                          setIssued({ kind: 'api_key', value: result.api_key });
                        }
                      })
                    }
                    onRotateSecret={() =>
                      run(async () => {
                        const result = await sourcesApi.rotateWebhookSecret(
                          tenantId ?? '',
                          source.id,
                        );
                        setIssued({ kind: 'webhook_secret', value: result.webhook_secret });
                      })
                    }
                    onDelete={() => run(() => sourcesApi.remove(tenantId ?? '', source.id))}
                    onShowDeliveries={() => void loadDeliveries(source.id)}
                  />
                ) : null}

                {deliveriesFor === source.id ? (
                  <div className="space-y-2 border-t pt-3">
                    <h3 className="text-sm font-medium">{t('webhook.title')}</h3>
                    <WebhookDeliveries
                      deliveries={deliveries}
                      busy={busy}
                      onRetry={async (deliveryId) => {
                        await run(() => webhookDeliveriesApi.retry(tenantId ?? '', deliveryId));
                        await loadDeliveries(source.id);
                      }}
                    />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
